import { ApolloCache, InMemoryCache } from '@apollo/client';

import {
  GetAccountingQresQuery,
  GetAscProjectsQuery,
  GetAscProjectTeamMembersQuery,
  GetAscProjectTpTicketsQuery,
  GetAscWagesQuery,
  GetPayrollQresQuery,
  GetRdProjectTeamMembersQuery,
  GetRdProjectTpTicketsQuery,
  GetRdProjectsQuery,
  GetSredProjectsQuery,
  Query,
  QueryGetAccountingQresArgs,
  QueryGetAscProjectsArgs,
  QueryGetAscProjectTeamMembersArgs,
  QueryGetAscProjectTpTicketsArgs,
  QueryGetAscWagesArgs,
  QueryGetPayrollQresArgs,
  QueryGetRdProjectTeamMembersArgs,
  QueryGetRdProjectTpTicketsArgs,
  QueryGetRdProjectsArgs,
  QueryGetSredProjectsArgs,
  StrictTypedTypePolicies,
} from '../graphql';

export const cache: InMemoryCache = new InMemoryCache({
  typePolicies: {
    AscWage: {
      keyFields: ['id', 'fiscalYearId', 'periodName'],
    },
    AscProjectTeamMember: {
      keyFields: ['contributorId', 'ascProjectId', 'fiscalYearId', 'periodName'],
    },
    RdProjectTeamMember: {
      keyFields: ['rdExpenseId', 'rdProjectId', 'fiscalYearId'],
    },
    AscGroupingTicketOrCollection: {
      keyFields: false,
    },
    RdGroupingTicketOrCollection: {
      keyFields: false,
    },
    AscProjectApprovalStatusResult: {
      keyFields: ['ascProjectId', 'fiscalYearId', 'periodName'],
    },
    AscPeriodLockStatus: {
      keyFields: ['fiscalYearId', 'periodName'],
    },
    Query: {
      fields: {
        // for paginated results we must specify merge rule so that the cache does not create
        // new fields for each request for more data but instead merges it into the current
        // request field in the cache
        getPayrollQres: {
          // specify variable fields that should cause a separate cache entry instead of a merge
          keyArgs: ['fiscalYearId', 'filter', 'sorting'] satisfies (keyof QueryGetPayrollQresArgs)[],
          // Concatenate existing object with incoming page
          merge: (
            existing: GetPayrollQresQuery['getPayrollQres'] | undefined,
            incoming: GetPayrollQresQuery['getPayrollQres'],
          ): GetPayrollQresQuery['getPayrollQres'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
        getAccountingQres: {
          // specify variable fields that should cause a separate cache entry instead of a merge
          keyArgs: ['fiscalYearId', 'filter', 'sorting'] satisfies (keyof QueryGetAccountingQresArgs)[],
          // Concatenate existing object with incoming page
          merge: (
            existing: GetAccountingQresQuery['getAccountingQres'] | undefined,
            incoming: GetAccountingQresQuery['getAccountingQres'],
          ): GetAccountingQresQuery['getAccountingQres'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
        getRdProjects: {
          // specify variable fields that should cause a separate cache entry instead of a merge
          keyArgs: ['fiscalYearId', 'filter', 'sorting'] satisfies (keyof QueryGetRdProjectsArgs)[],
          // Concatenate existing object with incoming page
          merge: (
            existing: GetRdProjectsQuery['getRdProjects'] | undefined,
            incoming: GetRdProjectsQuery['getRdProjects'],
          ): GetRdProjectsQuery['getRdProjects'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
        getSredProjects: {
          // specify variable fields that should cause a separate cache entry instead of a merge
          keyArgs: ['sredTaxCreditId', 'filter', 'sorting'] satisfies (keyof QueryGetSredProjectsArgs)[],
          // Concatenate existing object with incoming page
          merge: (
            existing: GetSredProjectsQuery['getSredProjects'] | undefined,
            incoming: GetSredProjectsQuery['getSredProjects'],
          ): GetSredProjectsQuery['getSredProjects'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
        getAscProjects: {
          // specify variable fields that should cause a separate cache entry instead of a merge
          keyArgs: [
            'companyId',
            'fiscalYearId',
            'periodName',
            'filter',
            'sorting',
          ] satisfies (keyof QueryGetAscProjectsArgs)[],
          // Concatenate existing object with incoming page
          merge: (
            existing: GetAscProjectsQuery['getAscProjects'] | undefined,
            incoming: GetAscProjectsQuery['getAscProjects'],
          ): GetAscProjectsQuery['getAscProjects'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
        getAscWages: {
          // specify variable fields that should cause a separate cache entry instead of a merge
          keyArgs: ['fiscalYearId', 'periodName', 'filter', 'sorting'] satisfies (keyof QueryGetAscWagesArgs)[],
          // Concatenate existing object with incoming page
          merge: (
            existing: GetAscWagesQuery['getAscWages'] | undefined,
            incoming: GetAscWagesQuery['getAscWages'],
          ): GetAscWagesQuery['getAscWages'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
        getAscProjectTpTickets: {
          keyArgs: [
            'ascProjectId',
            'fiscalYearId',
            'periodName',
            'filter',
            'sorting',
          ] satisfies (keyof QueryGetAscProjectTpTicketsArgs)[],
          merge: (
            existing: GetAscProjectTpTicketsQuery['getAscProjectTpTickets'] | undefined,
            incoming: GetAscProjectTpTicketsQuery['getAscProjectTpTickets'],
          ): GetAscProjectTpTicketsQuery['getAscProjectTpTickets'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
        getRdProjectTpTickets: {
          keyArgs: ['rdProjectId', 'filter', 'sorting'] satisfies (keyof QueryGetRdProjectTpTicketsArgs)[],
          merge: (
            existing: GetRdProjectTpTicketsQuery['getRdProjectTpTickets'] | undefined,
            incoming: GetRdProjectTpTicketsQuery['getRdProjectTpTickets'],
          ): GetRdProjectTpTicketsQuery['getRdProjectTpTickets'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
        getAscProjectTeamMembers: {
          // specify variable fields that should cause a separate cache entry instead of a merge
          keyArgs: [
            'ascProjectId',
            'fiscalYearId',
            'periodName',
            'filter',
            'sorting',
          ] satisfies (keyof QueryGetAscProjectTeamMembersArgs)[],
          // Concatenate existing object with incoming page
          merge: (
            existing: GetAscProjectTeamMembersQuery['getAscProjectTeamMembers'] | undefined,
            incoming: GetAscProjectTeamMembersQuery['getAscProjectTeamMembers'],
          ): GetAscProjectTeamMembersQuery['getAscProjectTeamMembers'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
        getRdProjectTeamMembers: {
          // specify variable fields that should cause a separate cache entry instead of a merge
          keyArgs: [
            'rdProjectId',
            'fiscalYearId',
            'filter',
            'sorting',
          ] satisfies (keyof QueryGetRdProjectTeamMembersArgs)[],
          // Concatenate existing object with incoming page
          merge: (
            existing: GetRdProjectTeamMembersQuery['getRdProjectTeamMembers'] | undefined,
            incoming: GetRdProjectTeamMembersQuery['getRdProjectTeamMembers'],
          ): GetRdProjectTeamMembersQuery['getRdProjectTeamMembers'] => {
            if (!incoming.pageInfo.hasPreviousPage) {
              return incoming;
            }
            const edges = [...(existing?.edges || []), ...incoming.edges];
            return {
              ...incoming,
              edges,
            };
          },
        },
      },
    },
    Company: {
      fields: {
        bulkTicketingDataUploads: {
          merge: (_existing, incoming) => incoming,
        },
      },
    },
    RdProject: {
      fields: {
        supportingDocuments: {
          merge: (_existing, incoming) => incoming,
        },
      },
    },
  } satisfies StrictTypedTypePolicies,
});

export const evictCache = (cache: ApolloCache<unknown>, fieldNames: (keyof Query)[]): void => {
  fieldNames.forEach((fieldName) => {
    cache.evict({ fieldName });
  });
  cache.gc();
};

/**
 * Use this when you make a change to project eligibility.
 */
export const evictQres = (): void => {
  evictCache(cache, [
    'getRdProjectTeamMembers',
    'getAccountingQres',
    'getAccountingQreSummary',
    'getPayrollQres',
    'getPayrollQreSummary',
    'getRdProjects',
  ]);
};

/**
 * Use this when you update payroll <> project allocations
 */
export const evictPayrollAllocation = (): void => {
  evictCache(cache, [
    'getRdProjectTeamMembers',
    'getPayrollQres',
    'getPayrollQreSummary',
    'getPayrollExpense',
    'getContributor',
  ]);
};

/**
 * Use this when you update accounting <> project allocations
 */
export const evictAccountingAllocation = (): void => {
  evictCache(cache, ['getAccountingQres', 'getAccountingQreSummary', 'getAccountingExpense']);
};

export const evictCapitalizedAllocation = (): void => {
  // Ideally we would specify exact `AscProjectPerContributor` field with id like `AscProjectPerContributor:${id}`,
  // for now we just evict the whole field
  cache.evict({ fieldName: 'AscProjectPerContributor' });
  evictCache(cache, ['getAscWages', 'getAscWageSummary', 'getContributor']);
};
